import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Phone, Facebook, MessageSquare, Mail, Link2, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, SITE } from "@/lib/api";

const renderInline = (text) =>
  text.split(/(\*[^*]+\*)/g).map((part, i) =>
    part.startsWith("*") && part.endsWith("*") && part.length > 2 ? <em key={`em-${i}`}>{part.slice(1, -1)}</em> : part
  );

export function ArticleBody({ body }) {
  const blocks = body.split(/\n\s*\n/).map((b) => b.trim()).filter(Boolean);
  return (
    <div className="journal-body" data-testid="journal-body">
      {blocks.map((b, i) =>
        b.startsWith("## ") ? (
          <h2 key={`b-${i}`} className="mt-14 mb-5 text-2xl md:text-3xl text-ocean" style={{ fontFamily: "Playfair Display, serif" }}>
            {b.slice(3)}
          </h2>
        ) : (
          <p key={`b-${i}`} className={`mb-6 text-[17px] md:text-lg leading-[1.85] text-[#2c3e44] whitespace-pre-line ${i === 0 ? "first-letter:text-6xl first-letter:float-left first-letter:mr-3 first-letter:mt-1 first-letter:leading-[0.8] first-letter:text-[#d4a373] first-letter:font-serif" : ""}`}>
            {renderInline(b)}
          </p>
        )
      )}
    </div>
  );
}

function ShareBar({ article, testid }) {
  const url = `${window.location.origin}/resources/journal/${article.slug}`;
  const text = `${article.title} — from the Champagne & Sunshine team`;
  const links = [
    { id: "facebook", label: "Facebook", Icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}` },
    { id: "text", label: "Text", Icon: MessageSquare, href: `sms:?&body=${encodeURIComponent(`${text} ${url}`)}` },
    { id: "email", label: "Email", Icon: Mail, href: `mailto:?subject=${encodeURIComponent(article.title)}&body=${encodeURIComponent(`Thought you'd find this useful:\n\n${text}\n${url}`)}` },
  ];
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch {
      toast.error("Couldn't copy — try the other buttons");
    }
  };
  const btn = "inline-flex items-center gap-2 px-4 py-2.5 text-sm border transition-colors";
  return (
    <div className="flex flex-wrap items-center gap-3" data-testid={testid}>
      <span className="text-xs uppercase tracking-[0.2em] text-current opacity-75 mr-1">Share</span>
      {links.map(({ id, label, Icon, href }) => (
        <a
          key={id}
          href={href}
          target={id === "facebook" ? "_blank" : undefined}
          rel="noopener noreferrer"
          className={`${btn} border-current/40 hover:bg-[#d4a373] hover:border-[#d4a373] hover:text-white`}
          style={{ borderColor: "currentColor" }}
          data-testid={`${testid}-${id}`}
        >
          <Icon size={14} /> {label}
        </a>
      ))}
      <button onClick={copy} className={`${btn} hover:bg-[#d4a373] hover:border-[#d4a373] hover:text-white`} style={{ borderColor: "currentColor" }} data-testid={`${testid}-copy`}>
        <Link2 size={14} /> Copy link
      </button>
    </div>
  );
}

function SubscribeBox({ slug }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");

  const submit = async (e) => {
    e.preventDefault();
    setState("sending");
    try {
      const { data } = await api.post("/journal/subscribe", { name, email, source: `essay:${slug}` });
      setState(data.already ? "already" : "done");
    } catch {
      setState("idle");
      toast.error("Please enter a valid email");
    }
  };

  if (state === "done" || state === "already") {
    return (
      <div className="mt-16 p-8 md:p-10 bg-white border border-[#e8c79a] flex items-start gap-4" data-testid="journal-subscribe-success">
        <CheckCircle2 className="text-[#d4a373] shrink-0" size={26} />
        <div>
          <p className="text-2xl text-ocean" style={{ fontFamily: "Playfair Display, serif" }}>
            {state === "already" ? "You're already on the list." : "You're on the list. Cheers!"}
          </p>
          <p className="mt-2 text-soft text-sm">We'll email you when a new essay goes live. No spam, ever.</p>
        </div>
      </div>
    );
  }

  const input = "w-full border border-[#E5E1DA] bg-white px-4 py-3 text-sm outline-none focus:border-[#d4a373] min-w-0";
  return (
    <form onSubmit={submit} className="mt-16 p-8 md:p-10 border border-[#e8c79a]" style={{ background: "linear-gradient(140deg, #fdf1de 0%, #f8e6cf 100%)" }} data-testid="journal-subscribe-form">
      <p className="overline">The Field Journal, delivered</p>
      <p className="mt-3 text-2xl md:text-3xl text-ocean" style={{ fontFamily: "Playfair Display, serif" }}>Get new essays by email.</p>
      <p className="mt-2 text-soft text-sm">Straight talk on pricing, buying, and the South Bay market — only when there's something worth reading.</p>
      <div className="mt-6 grid gap-3 sm:grid-cols-[1fr_1.4fr_auto]">
        <input className={input} placeholder="First name" value={name} onChange={(e) => setName(e.target.value)} data-testid="journal-subscribe-name" />
        <input className={input} type="email" required placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="journal-subscribe-email" />
        <button className="btn-primary inline-flex items-center justify-center" disabled={state === "sending"} data-testid="journal-subscribe-submit">
          <Send size={14} className="mr-2" /> {state === "sending" ? "Adding…" : "Sign me up"}
        </button>
      </div>
    </form>
  );
}

export default function JournalArticle() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [next, setNext] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setArticle(null);
    setMissing(false);
    api.get(`/journal/${slug}`).then((r) => setArticle(r.data)).catch(() => setMissing(true));
    api.get("/journal").then((r) => {
      const list = r.data;
      const idx = list.findIndex((a) => a.slug === slug);
      setNext(list.length > 1 ? list[(idx + 1) % list.length] : null);
    }).catch(() => setNext(null));
  }, [slug]);

  if (missing) {
    return (
      <div className="container-edge py-32 text-center" data-testid="journal-not-found">
        <p className="text-3xl text-ocean" style={{ fontFamily: "Playfair Display, serif" }}>That essay isn't available.</p>
        <Link to="/resources" className="btn-primary mt-8 inline-flex">Back to the Field Journal</Link>
      </div>
    );
  }
  if (!article) return <div className="min-h-[70vh] bg-[#f8f5ee] animate-pulse" data-testid="journal-loading" />;

  return (
    <article data-testid={`journal-article-${article.slug}`}>
      <header className="relative overflow-hidden" style={{ background: "linear-gradient(135deg, #1e4d5d 0%, #2c5865 60%, #b8814f 135%)" }}>
        <div className="container-edge pt-20 pb-16 md:pt-28 md:pb-24 text-white max-w-5xl mx-auto">
          <Link to="/resources" className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-[#e8c79a] transition-colors" data-testid="journal-back-link">
            <ArrowLeft size={14} /> The Field Journal
          </Link>
          <p className="mt-10 overline" style={{ color: "#e8c79a" }}>{[article.topic, `${article.read} read`].filter(Boolean).join(" · ")}</p>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl leading-[1.08] text-white" style={{ fontFamily: "Playfair Display, serif", color: "#fff" }} data-testid="journal-title">
            {article.title}
          </h1>
          {article.dek && <p className="mt-6 text-base md:text-lg text-white/85 font-light max-w-2xl">{article.dek}</p>}
          <p className="mt-8 text-xs tracking-[0.2em] uppercase text-white/70">By the Champagne &amp; Sunshine Team · KW L.A. Harbor</p>
          <div className="mt-8 text-white"><ShareBar article={article} testid="journal-share-top" /></div>
        </div>
      </header>

      <div className="bg-[#f8f5ee]">
        <div className="container-edge py-16 md:py-24 max-w-4xl mx-auto">
          <ArticleBody body={article.body} />

          <div className="mt-12 pt-8 border-t border-soft text-ocean">
            <p className="text-sm text-soft mb-4">Know someone who should read this?</p>
            <ShareBar article={article} testid="journal-share-bottom" />
          </div>

          <SubscribeBox slug={article.slug} />

          <div className="mt-10 p-8 md:p-10 text-white" style={{ backgroundColor: "#1e4d5d" }} data-testid="journal-cta">
            <p className="overline" style={{ color: "#e8c79a" }}>Your next move</p>
            <p className="mt-3 text-2xl md:text-3xl text-white" style={{ fontFamily: "Playfair Display, serif" }}>Questions? We're one call away.</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to={article.cta_to} className="btn-secondary inline-flex items-center" data-testid="journal-cta-primary">
                {article.cta_label} <ArrowUpRight size={14} className="ml-2" />
              </Link>
              <a href={SITE.teamPhoneHref} className="inline-flex items-center px-6 py-3 border border-white/60 text-white text-sm hover:bg-white hover:text-[#1e4d5d] transition-colors" data-testid="journal-cta-call">
                <Phone size={14} className="mr-2" /> {SITE.teamPhone}
              </a>
            </div>
          </div>

          {next && (
            <Link to={`/resources/journal/${next.slug}`} className="mt-14 block group border-t border-soft pt-10" data-testid="journal-next-link">
              <p className="overline">Read next</p>
              <p className="mt-3 text-2xl md:text-3xl text-ocean group-hover:text-sand transition-colors" style={{ fontFamily: "Playfair Display, serif" }}>
                {next.title}
              </p>
            </Link>
          )}
        </div>
      </div>
    </article>
  );
}
